import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { PaymentService } from '../../services/payments/payment.service'
import { CommonService } from '../../services/common/common.service'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { Tenant, CurrentUser } from '../../models/tenant-dialog.models'
import { ICONS } from '../../constants/icons'

@Component({
  selector: 'app-tenant-dialog',
  templateUrl: './tenant-dialog.component.html',
  styleUrls: ['./tenant-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class TenantDialogComponent implements OnInit, OnDestroy {
  readonly ICONS = ICONS
  tenantList: Tenant[] = []
  selectedTenant: Tenant | null = null
  currentTenant: CurrentUser | null = null
  private destroy$ = new Subject<void>()

  constructor(
    private dialogRef: MatDialogRef<TenantDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private paymentService: PaymentService,
    private commonService: CommonService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initializeDialog()
    this.loadTenantList()
  }

  private initializeDialog(): void {
    if (this.data && this.data.currentUser) {
      this.currentTenant = this.data.currentUser
    }
  }

  private loadTenantList(): void {
    this.paymentService
      .fetchTenantList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: any) => {
        if (response && response.data) {
          this.tenantList = response.data
          this.setInitialTenantSelection()
        }
      })
  }

  private setInitialTenantSelection(): void {
    if (this.currentTenant && this.currentTenant.tenantName) {
      const currentTenantInList = this.tenantList.find(
        (tenant: Tenant) =>
          tenant.tenant_name === this.currentTenant?.tenantName
      )
      if (currentTenantInList) {
        this.selectedTenant = currentTenantInList
      } else if (this.tenantList.length > 0) {
        this.selectedTenant = this.tenantList[0]
      }
    } else if (this.tenantList.length > 0) {
      // Fallback: Set first tenant as default selection
      this.selectedTenant = this.tenantList[0]
    }
  }

  onTenantChange(tenant: Tenant): void {
    this.selectedTenant = tenant
  }

  /**
   * Check if selected tenant is the current logged-in tenant
   */
  isCurrentTenant(): boolean {
    if (!this.selectedTenant || !this.currentTenant) {
      return false
    }
    return this.selectedTenant.tenant_name === this.currentTenant.tenantName
  }

  closeDialog(): void {
    this.dialogRef.close()
  }

  switchTenant(): void {
    if (this.selectedTenant) {
      if (this.selectedTenant.tenant_id) {
        sessionStorage.setItem('tenant_id', this.selectedTenant.tenant_id)
      }

      // Clear isLogin flag and user data to trigger fresh navigation based on new tenant's user type
      sessionStorage.removeItem('isLogin')
      localStorage.removeItem('user')
      localStorage.removeItem('user_id')

      this.commonService
        .getUserInfo()
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          // Navigate to the correct route based on user_type so the page reloads
          // at the right URL and the header tab is highlighted correctly.
          // Unknown/missing user_type falls back to the current pathname rather
          // than silently treating it as an approver (user_type !== 1).
          const userType = res?.user_type
          const targetRoute =
            userType === 1
              ? '/manage-deductions/new-remittance'
              : userType != null
                ? '/manage-deductions/approvals'
                : window.location.pathname
          this.router.navigateByUrl(targetRoute).then(() => {
            window.location.reload()
          })
        })
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
